import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';

const dateTime = (value) => value ? new Date(value).toLocaleString('en-GB') : '—';

const STATUS_STYLES = {
    open: 'bg-red-50 text-red-600 border-red-200',
    reviewing: 'bg-amber-50 text-amber-700 border-amber-200',
    resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dismissed: 'bg-slate-100 text-slate-500 border-slate-200',
};

/**
 * Moderation desk. A report is one person's account of events, so the row
 * leads with who is accused and how many separate complaints they have —
 * one is noise, five is the thing worth ruling on.
 */
function AdminReports() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('open');
    const [query, setQuery] = useState('');
    const [openId, setOpenId] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const params = {};
            if (filter !== 'all') params.status = filter;
            if (query) params.q = query;
            const res = await api.get('/api/admin/reports/', { params });
            setData(res.data);
        } finally { setLoading(false); }
    }, [filter, query]);

    useEffect(() => {
        const timer = setTimeout(load, query ? 300 : 0);
        return () => clearTimeout(timer);
    }, [load, query]);

    const decide = async (reportId, action, adminNote) => {
        setBusy(true);
        try {
            await api.post('/api/admin/reports/', {
                report_id: reportId, action, admin_note: adminNote,
            });
            await load();
        } finally { setBusy(false); }
    };

    if (loading) return <div className="flex justify-center py-10"><div className="spinner" /></div>;
    if (!data) return <p className="text-sm text-slate-400 text-center py-10">{t('admin.unauthorizedMsg')}</p>;

    const filters = [
        { key: 'open', label: `${t('reports.filterOpen')} (${data.counts.open})` },
        { key: 'reviewing', label: `${t('reports.filterReviewing')} (${data.counts.reviewing})` },
        { key: 'resolved', label: t('reports.filterResolved') },
        { key: 'dismissed', label: t('reports.filterDismissed') },
        { key: 'all', label: `${t('reports.filterAll')} (${data.counts.total})` },
    ];

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {['open', 'reviewing', 'resolved', 'dismissed'].map(key => (
                    <div key={key} className="rounded-xl bg-white border border-slate-200 p-3">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                            {t(`reports.status_${key}`)}
                        </p>
                        <p className={`text-lg font-black tabular-nums ${key === 'open' && data.counts.open > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                            {data.counts[key]}
                        </p>
                    </div>
                ))}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
                <input className="input py-2 text-sm" value={query}
                    placeholder={t('reports.searchPlaceholder')}
                    onChange={(e) => setQuery(e.target.value)} />
                <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                    {filters.map(item => (
                        <button key={item.key} onClick={() => setFilter(item.key)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap border transition ${filter === item.key
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-slate-500 border-slate-200 hover:border-primary-300'}`}>
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>

            {data.reports.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-10">{t('reports.empty')}</p>
            ) : (
                <div className="space-y-2">
                    {data.reports.map(report => (
                        <ReportRow key={report.id} report={report}
                            open={openId === report.id}
                            onToggle={() => setOpenId(openId === report.id ? null : report.id)}
                            onDecide={decide} busy={busy} t={t} />
                    ))}
                </div>
            )}
        </div>
    );
}

function ReportRow({ report, open, onToggle, onDecide, busy, t }) {
    const [note, setNote] = useState(report.admin_note || '');
    const closed = ['resolved', 'dismissed'].includes(report.status);

    return (
        <div className={`rounded-2xl border bg-white overflow-hidden ${report.status === 'open' ? 'border-red-200' : 'border-slate-200'}`}>
            <button onClick={onToggle} className="w-full text-left p-3.5 flex items-center gap-3">
                <img src={report.reported_user?.profile_picture || '/logo192.png'} alt=""
                    className="w-9 h-9 rounded-full object-cover ring-2 ring-slate-100 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-bold text-slate-800 truncate">
                            {report.reported_user?.username}
                        </p>
                        <span className="text-[10px] text-slate-400">
                            {report.reported_user?.account_type}
                        </span>
                        {report.reports_against_total > 1 && (
                            <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[9px] font-black whitespace-nowrap">
                                {report.reports_against_total}× {t('reports.reportedBadge')}
                            </span>
                        )}
                    </div>
                    <p className="text-[11px] text-slate-500 truncate">
                        {t(`report.reason_${report.reason}`)}
                    </p>
                    <p className="text-[10px] text-slate-400">
                        {t('reports.by')} {report.reporter?.username || t('reports.deletedUser')} · {dateTime(report.created_at)}
                    </p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border flex-shrink-0 ${STATUS_STYLES[report.status]}`}>
                    {t(`reports.status_${report.status}`)}
                </span>
            </button>

            {open && (
                <div className="px-3.5 pb-3.5 space-y-3 border-t border-slate-100 pt-3">
                    {report.description ? (
                        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                                {t('reports.reporterSays')}
                            </p>
                            <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                                {report.description}
                            </p>
                        </div>
                    ) : (
                        <p className="text-xs text-slate-400 italic">{t('reports.noDetail')}</p>
                    )}

                    {closed && report.reviewed_by && (
                        <p className="text-[11px] text-slate-500">
                            {t('reports.ruledBy')} <strong>{report.reviewed_by}</strong> · {dateTime(report.reviewed_at)}
                            {report.admin_note && <span className="block italic mt-0.5">"{report.admin_note}"</span>}
                        </p>
                    )}

                    {!closed && (
                        <>
                            <input className="input py-2 text-xs" value={note}
                                placeholder={t('reports.notePlaceholder')}
                                onChange={(e) => setNote(e.target.value)} />
                            <div className="flex flex-wrap gap-2">
                                {report.status === 'open' && (
                                    <button onClick={() => onDecide(report.id, 'review', note)} disabled={busy}
                                        className="btn-secondary flex-1 py-2 text-xs min-w-[110px]">
                                        {t('reports.markReviewing')}
                                    </button>
                                )}
                                <button onClick={() => onDecide(report.id, 'resolve', note)} disabled={busy}
                                    className="btn-success flex-1 py-2 text-xs min-w-[110px]">
                                    {t('reports.actionTaken')}
                                </button>
                                <button onClick={() => onDecide(report.id, 'dismiss', note)} disabled={busy}
                                    className="px-3 py-2 rounded-xl text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition flex-1 min-w-[110px]">
                                    {t('reports.dismiss')}
                                </button>
                            </div>
                            <p className="text-[10px] text-slate-400 leading-relaxed">{t('reports.actionHint')}</p>
                        </>
                    )}

                    {closed && (
                        <button onClick={() => onDecide(report.id, 'reopen', note)} disabled={busy}
                            className="w-full py-2 rounded-xl text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition">
                            {t('reports.reopen')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default AdminReports;
